/**
 * Weight trend math. Deterministic and unit-tested — the AI explains these
 * numbers, it never computes them.
 *
 * - Trend: trailing moving average of daily weigh-in averages over a calendar
 *   window (14 days by default). Multiple weigh-ins on one day are averaged
 *   first. Days with no weigh-in stay in the average until they age out of the
 *   window; a gap longer than the window leaves a break instead of a made-up value.
 *   Early history uses whatever days the window already contains.
 * - Rate: least-squares slope of that trend over the recent window, so projections
 *   follow the line on the chart. The uncertainty comes from the raw weigh-ins,
 *   so smoothing doesn't pretend the range is tighter than the scale is.
 * - Projection: goal date only when moving toward the goal, with an honest range.
 *   Weight at 30/60/90 days follows the same rate whenever the trend is current.
 * - Stretch: a weigh-in can be marked as a starting weight. The latest marked one
 *   (on or before today) starts the current stretch, and every number above counts
 *   from it. The moving average restarts there: earlier weigh-ins never enter its
 *   window, the start day's trend is the starting weigh-in itself, and the window
 *   fills over the following days as it does at the start of history (the rate
 *   prefers full-window days counted from the start). With nothing marked, all
 *   history counts. Callers get the window from stretchOf. The rapid-loss safety
 *   flag is on when the stretch since a start or all history is losing that fast,
 *   so starting over can't hide it and older weigh-ins can't flatten it away.
 * - Headline: the number shown as current weight is the recent low (the lowest
 *   single weigh-in in the window), not the trend. The trend, rate, projection
 *   and period changes are unchanged.
 */
import { addDays, diffDays, type ISODate } from './dates';

export interface WeighIn {
  date: ISODate;
  kg: number;
}

/** A weigh-in that may be marked as the starting weight of a stretch. */
export interface MarkedWeighIn extends WeighIn {
  startingWeight?: boolean;
}

export interface TrendPoint {
  date: ISODate;
  kg: number;
}

export const DEFAULT_TREND_WINDOW_DAYS = 14;
export const MIN_TREND_WINDOW_DAYS = 3;
export const MAX_TREND_WINDOW_DAYS = 60;
export const RATE_WINDOW_DAYS = 28;
/** Losing more than 1% of body weight per week, sustained, triggers a safety notice. */
export const RAPID_LOSS_FRACTION_PER_WEEK = 0.01;
const MIN_RATE_SPAN_DAYS = 10;
const MAX_PROJECTION_DAYS = 730;
/** How far a period comparison may snap to a nearby trend point when a day is missing. */
const TREND_MATCH_SLACK_DAYS = 3;
export const FORECAST_DAYS = [30, 60, 90] as const;

/** Clamp to the allowed window. Non-numbers become the default. */
export function normalizeTrendWindow(days: number | null | undefined): number {
  if (typeof days !== 'number' || !Number.isFinite(days)) return DEFAULT_TREND_WINDOW_DAYS;
  return Math.min(MAX_TREND_WINDOW_DAYS, Math.max(MIN_TREND_WINDOW_DAYS, Math.round(days)));
}

/**
 * The weigh-in the current stretch starts from: the latest one marked as a starting
 * weight, on or before `today`. Earlier marks are kept but don't count. Null when
 * nothing is marked (or the marked weigh-ins were deleted): then all history counts.
 */
export function stretchStart<T extends MarkedWeighIn>(weighIns: readonly T[], today: ISODate): T | null {
  let start: T | null = null;
  for (const w of weighIns) {
    if (w.startingWeight && w.date <= today && (start === null || w.date > start.date)) start = w;
  }
  return start;
}

/** The weigh-ins a stretch counts: those on or after `since`. Without a start, all of them. */
export function fromStart<T extends WeighIn>(weighIns: readonly T[], since: ISODate | null | undefined): readonly T[] {
  return since ? weighIns.filter((w) => w.date >= since) : weighIns;
}

export interface Stretch<T> {
  /** The starting weigh-in, or null when all history counts. */
  start: T | null;
  since: ISODate | null;
  /** The weigh-ins the trend and summary count. */
  counted: readonly T[];
}

/** The current stretch: everything weight-related counts `counted`, and summarizeWeight takes `since`. */
export function stretchOf<T extends MarkedWeighIn>(weighIns: readonly T[], today: ISODate): Stretch<T> {
  const start = stretchStart(weighIns, today);
  const since = start?.date ?? null;
  return { start, since, counted: fromStart(weighIns, since) };
}

export function dailyAverages(weighIns: readonly WeighIn[]): WeighIn[] {
  const byDay = new Map<ISODate, { sum: number; n: number }>();
  for (const w of weighIns) {
    if (!Number.isFinite(w.kg) || w.kg <= 0) continue;
    const cur = byDay.get(w.date) ?? { sum: 0, n: 0 };
    cur.sum += w.kg;
    cur.n += 1;
    byDay.set(w.date, cur);
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([date, { sum, n }]) => ({ date, kg: sum / n }));
}

/**
 * One trend value per calendar day the trailing window still contains a weigh-in.
 *
 * The window is `windowDays` calendar days ending on that day, inclusive. Only
 * days that have a weigh-in are averaged — empty days are not filled with zero
 * or with the previous weight. While an earlier weigh-in is still inside the
 * window, later empty days keep a point (the average of what's left). Once the
 * window is empty, those days are omitted so the line breaks across a long gap.
 * The series starts on the first weigh-in, using a partial window until N days exist.
 *
 * `through` extends the series past the last weigh-in (typically to today) but
 * never past the last day whose window still overlaps a weigh-in.
 */
export function dailyTrend(weighIns: readonly WeighIn[], windowDays = DEFAULT_TREND_WINDOW_DAYS, through?: ISODate): TrendPoint[] {
  const window = normalizeTrendWindow(windowDays);
  const days = dailyAverages(weighIns);
  const first = days[0];
  const last = days[days.length - 1];
  if (!first || !last) return [];

  let end = through ?? last.date;
  if (end < first.date) return [];
  const lastCovered = addDays(last.date, window - 1);
  if (end > lastCovered) end = lastCovered;

  const prefix: number[] = [0];
  for (const day of days) prefix.push(prefix[prefix.length - 1]! + day.kg);

  const out: TrendPoint[] = [];
  let iLeft = 0;
  let iRight = 0;
  for (let d = first.date; d <= end; d = addDays(d, 1)) {
    const windowStart = addDays(d, -(window - 1));
    while (iLeft < days.length && days[iLeft]!.date < windowStart) iLeft++;
    while (iRight < days.length && days[iRight]!.date <= d) iRight++;
    const n = iRight - iLeft;
    if (n <= 0) continue;
    out.push({ date: d, kg: (prefix[iRight]! - prefix[iLeft]!) / n });
  }
  return out;
}

export interface LinearFit {
  slope: number;
  intercept: number;
  slopeSE: number;
  n: number;
}

export function linearFit(xs: number[], ys: number[]): LinearFit | null {
  const n = Math.min(xs.length, ys.length);
  if (n < 3) return null;
  let sx = 0;
  let sy = 0;
  for (let i = 0; i < n; i++) {
    sx += xs[i]!;
    sy += ys[i]!;
  }
  const mx = sx / n;
  const my = sy / n;
  let sxx = 0;
  let sxy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i]! - mx;
    sxx += dx * dx;
    sxy += dx * (ys[i]! - my);
  }
  if (sxx === 0) return null;
  const slope = sxy / sxx;
  const intercept = my - slope * mx;
  let sse = 0;
  for (let i = 0; i < n; i++) {
    const r = ys[i]! - (intercept + slope * xs[i]!);
    sse += r * r;
  }
  const slopeSE = Math.sqrt(sse / Math.max(1, n - 2) / sxx);
  return { slope, intercept, slopeSE, n };
}

export interface Rate {
  kgPerWeek: number;
  seKgPerWeek: number;
  spanDays: number;
  n: number;
}

/**
 * Weekly slope of the trend over the last `RATE_WINDOW_DAYS`, ending at `today`.
 * Needs weigh-ins spanning at least 10 days. The slope prefers the part of the
 * average whose window is already full, so the first partial days don't flatten it.
 */
export function weeklyRate(weighIns: readonly WeighIn[], today: ISODate, trendWindowDays = DEFAULT_TREND_WINDOW_DAYS): Rate | null {
  const window = normalizeTrendWindow(trendWindowDays);
  const averages = dailyAverages(weighIns);
  const trend = dailyTrend(weighIns, window, today);
  if (averages.length === 0 || trend.length === 0) return null;

  const from = addDays(today, -RATE_WINDOW_DAYS);
  const weighed = averages.filter((w) => w.date > from && w.date <= today);
  const w0 = weighed[0];
  const w1 = weighed[weighed.length - 1];
  if (!w0 || !w1 || weighed.length < 3) return null;
  const spanDays = diffDays(w1.date, w0.date);
  if (spanDays < MIN_RATE_SPAN_DAYS) return null;

  const recent = trend.filter((t) => t.date > from && t.date <= today);
  const matureFrom = addDays(averages[0]!.date, window - 1);
  const mature = recent.filter((t) => t.date >= matureFrom);
  const matureSpan = mature.length >= 2 ? diffDays(mature[mature.length - 1]!.date, mature[0]!.date) : 0;
  const sample = mature.length >= 3 && matureSpan >= MIN_RATE_SPAN_DAYS ? mature : recent;
  const first = sample[0];
  const last = sample[sample.length - 1];
  if (!first || !last || diffDays(last.date, first.date) < MIN_RATE_SPAN_DAYS) return null;

  const fit = linearFit(
    sample.map((t) => diffDays(t.date, first.date)),
    sample.map((t) => t.kg),
  );
  if (!fit) return null;

  const rawFit = linearFit(
    weighed.map((w) => diffDays(w.date, w0.date)),
    weighed.map((w) => w.kg),
  );

  return {
    kgPerWeek: fit.slope * 7,
    seKgPerWeek: (rawFit?.slopeSE ?? fit.slopeSE) * 7,
    spanDays,
    n: rawFit?.n ?? fit.n,
  };
}

export interface Projection {
  date: ISODate;
  earliest: ISODate;
  /** null when the slow end of the range isn't moving toward the goal. */
  latest: ISODate | null;
}

/** Kg per day of uncertainty: an 80% interval on the slope, and never narrower than ±20% of it. */
export function projectionSpreadPerDay(kgPerWeek: number, seKgPerWeek: number): number {
  const perDay = kgPerWeek / 7;
  return Math.max(1.28 * (seKgPerWeek / 7), Math.abs(perDay) * 0.2);
}

export function projectGoal(currentKg: number, goalKg: number, rate: Rate, today: ISODate): Projection | null {
  const remaining = goalKg - currentKg;
  if (Math.abs(remaining) < 0.25) return null;
  const perDay = rate.kgPerWeek / 7;
  if (perDay === 0 || Math.sign(perDay) !== Math.sign(remaining)) return null;
  const days = remaining / perDay;
  if (!Number.isFinite(days) || days <= 0 || days > MAX_PROJECTION_DAYS) return null;

  const spread = projectionSpreadPerDay(rate.kgPerWeek, rate.seKgPerWeek);
  const fast = Math.abs(perDay) + spread;
  const slow = Math.abs(perDay) - spread;
  const earliestDays = Math.abs(remaining) / fast;
  const latestDays = slow > 0 ? Math.abs(remaining) / slow : Infinity;
  return {
    date: addDays(today, Math.round(days)),
    earliest: addDays(today, Math.round(earliestDays)),
    latest: latestDays <= MAX_PROJECTION_DAYS ? addDays(today, Math.round(latestDays)) : null,
  };
}

export interface WeightForecast {
  days: number;
  date: ISODate;
  kg: number;
  lowKg: number;
  highKg: number;
}

/** Where the trend is headed if the current weekly rate holds. Null without a usable rate. */
export function projectWeights(currentKg: number, rate: Rate, today: ISODate): WeightForecast[] | null {
  const perDay = rate.kgPerWeek / 7;
  if (!Number.isFinite(perDay) || !Number.isFinite(currentKg)) return null;
  const spread = projectionSpreadPerDay(rate.kgPerWeek, rate.seKgPerWeek);
  const out: WeightForecast[] = [];
  for (const days of FORECAST_DAYS) {
    const kg = currentKg + perDay * days;
    const band = spread * days;
    if (!Number.isFinite(kg) || !Number.isFinite(band) || kg < 20 || kg > 400) continue;
    out.push({ days, date: addDays(today, days), kg, lowKg: kg - band, highKg: kg + band });
  }
  return out.length > 0 ? out : null;
}

export interface TrendChanges {
  /** Trend now minus the trend this many days ago. Null without a trend that far back. */
  d7: number | null;
  d30: number | null;
  d90: number | null;
  /** Trend now minus the first trend point. Null until the trend spans two days. */
  all: number | null;
}

function closestPoint(trend: TrendPoint[], date: ISODate): { point: TrendPoint; distance: number } | null {
  let best: TrendPoint | null = null;
  let bestDist = Infinity;
  for (const t of trend) {
    const dist = Math.abs(diffDays(t.date, date));
    if (dist < bestDist || (dist === bestDist && best !== null && t.date < best.date)) {
      best = t;
      bestDist = dist;
    }
  }
  return best ? { point: best, distance: bestDist } : null;
}

/** Trend now versus the trend at the start of each period. Missing history stays null. */
export function trendChanges(trend: TrendPoint[], today: ISODate): TrendChanges {
  const empty: TrendChanges = { d7: null, d30: null, d90: null, all: null };
  let now: TrendPoint | null = null;
  for (const t of trend) {
    if (t.date <= today) now = t;
    else break;
  }
  const first = trend[0];
  if (!now || !first) return empty;
  const all = now.date > first.date ? now.kg - first.kg : null;

  const period = (days: number): number | null => {
    const target = addDays(today, -days);
    // A later start would understate the period, so a short history stays blank.
    if (first.date > target) return null;
    const exact = trend.find((t) => t.date === target);
    if (exact) return now.kg - exact.kg;
    const near = closestPoint(trend, target);
    if (!near || near.distance > TREND_MATCH_SLACK_DAYS) return null;
    return now.kg - near.point.kg;
  };

  return { d7: period(7), d30: period(30), d90: period(90), all };
}

export interface RecentLow {
  kg: number;
  date: ISODate;
  /**
   * `window` — lowest weigh-in inside the trend window.
   * `latest` — that window had no weigh-in, so this is the latest one instead.
   */
  source: 'window' | 'latest';
}

/**
 * Lowest single weigh-in in the `windowDays` calendar days ending on `today`
 * (inclusive). Same-day readings are not averaged: the lowest one counts, and
 * a tie keeps the later date. Weigh-ins after today are ignored, as are
 * non-positive ones.
 *
 * `since` is the current stretch's start (see stretchOf). Weigh-ins before it
 * don't count, even when they fall inside the window. An empty window falls
 * back to the latest counted weigh-in. Null when nothing on or before today
 * qualifies — show the trend instead.
 */
export function recentLow(
  weights: readonly WeighIn[],
  today: ISODate,
  windowDays = DEFAULT_TREND_WINDOW_DAYS,
  since: ISODate | null = null,
): RecentLow | null {
  const window = normalizeTrendWindow(windowDays);
  const windowStart = addDays(today, -(window - 1));
  let low: WeighIn | null = null;
  let latest: WeighIn | null = null;
  for (const w of weights) {
    if (!Number.isFinite(w.kg) || w.kg <= 0 || w.date > today) continue;
    if (since && w.date < since) continue;
    if (!latest || w.date > latest.date) latest = w;
    if (w.date < windowStart) continue;
    if (!low || w.kg < low.kg || (w.kg === low.kg && w.date > low.date)) low = w;
  }
  if (low) return { kg: low.kg, date: low.date, source: 'window' };
  if (latest) return { kg: latest.kg, date: latest.date, source: 'latest' };
  return null;
}

export interface WeightSummary {
  /** The day the current stretch started (a marked starting weight). Null when all history counts. */
  since: ISODate | null;
  /** The first weigh-in counted: the starting weight when one is marked. */
  startKg: number | null;
  startDate: ISODate | null;
  latestKg: number | null;
  latestDate: ISODate | null;
  trendKg: number | null;
  /**
   * Lowest single weigh-in in the trend window, for the headline. `latest` when
   * that window is empty. Null when the stretch has no weigh-in — show `trendKg`.
   * Not an input to the trend, rate, projection, or period changes.
   */
  recentLow: RecentLow | null;
  /** Trend change since the start (all time without one). Same value as `changes.all`. */
  changeKg: number | null;
  changes: TrendChanges;
  goalKg: number | null;
  toGoKg: number | null;
  percentComplete: number | null;
  rate: Rate | null;
  projection: Projection | null;
  /** Null when the trend isn't defined today or the rate isn't reliable yet. */
  forecasts: WeightForecast[] | null;
  flags: {
    /**
     * Losing more than 1% of body weight a week. With a starting weigh-in, either the
     * stretch since that day or all history can turn this on. With nothing marked, it
     * is the all-history rate.
     */
    rapidLoss: boolean;
    /**
     * The rapid loss only shows with weigh-ins from before the start (the stretch alone
     * isn't losing that fast, or is too short to tell), as after a change of scale.
     */
    rapidLossBeforeStart: boolean;
    /** Latest weigh-in minus the trend that day, when the gap is notable (water, sodium, timing). */
    spikeKg: number | null;
    stale: boolean;
  };
}

function losingTooFast(rate: Rate | null, trendKg: number | null): boolean {
  return (
    rate !== null &&
    trendKg !== null &&
    rate.spanDays >= 21 &&
    rate.kgPerWeek < 0 &&
    Math.abs(rate.kgPerWeek) > trendKg * RAPID_LOSS_FRACTION_PER_WEEK
  );
}

/**
 * Everything counts from `since` (the current stretch's start, see stretchOf); null
 * counts all history. The rapid-loss flag uses that stretch and, when a start is
 * marked, all history as well: either rate over the threshold turns it on.
 * `recentLow` is the headline weigh-in only; it does not move the other numbers.
 */
export function summarizeWeight(
  weighIns: readonly WeighIn[],
  goalKg: number | null,
  today: ISODate,
  trendWindowDays = DEFAULT_TREND_WINDOW_DAYS,
  since: ISODate | null = null,
): WeightSummary {
  const window = normalizeTrendWindow(trendWindowDays);
  const stretch = fromStart(weighIns, since);
  const days = dailyAverages(stretch);
  const trend = dailyTrend(stretch, window, today);
  const start = days[0] ?? null;
  const latest = days[days.length - 1] ?? null;
  const latestTrend = trend[trend.length - 1] ?? null;
  const trendNow = latestTrend?.kg ?? null;
  const current = latestTrend?.date === today;
  const changes = trendChanges(trend, today);
  const rate = weeklyRate(stretch, today, window);

  let toGoKg: number | null = null;
  let percentComplete: number | null = null;
  if (goalKg !== null && trendNow !== null) {
    toGoKg = goalKg - trendNow;
    if (start && Math.abs(start.kg - goalKg) > 0.01) {
      const done = (start.kg - trendNow) / (start.kg - goalKg);
      percentComplete = Math.round(Math.min(1, Math.max(0, done)) * 100);
    }
  }

  const trendOnLatest = latest ? trend.find((t) => t.date === latest.date) : undefined;
  const spike = latest && trendOnLatest ? latest.kg - trendOnLatest.kg : null;
  // Either rate can trip the safety flag. All history, so a new stretch can't hide
  // fast loss. The stretch itself, so older weigh-ins can't flatten a fast loss away
  // (a new scale that reads higher, marked as the start).
  const stretchLosingFast = losingTooFast(rate, trendNow);
  let rapidLoss = stretchLosingFast;
  if (since) {
    const allTrend = dailyTrend(weighIns, window, today);
    const allHistoryLosingFast = losingTooFast(weeklyRate(weighIns, today, window), allTrend[allTrend.length - 1]?.kg ?? null);
    rapidLoss = stretchLosingFast || allHistoryLosingFast;
  }

  return {
    since,
    startKg: start?.kg ?? null,
    startDate: start?.date ?? null,
    latestKg: latest?.kg ?? null,
    latestDate: latest?.date ?? null,
    trendKg: trendNow,
    recentLow: recentLow(weighIns, today, window, since),
    changeKg: changes.all,
    changes,
    goalKg,
    toGoKg,
    percentComplete,
    rate,
    projection: current && trendNow !== null && rate && goalKg !== null ? projectGoal(trendNow, goalKg, rate, today) : null,
    forecasts: current && trendNow !== null && rate ? projectWeights(trendNow, rate, today) : null,
    flags: {
      rapidLoss,
      rapidLossBeforeStart: rapidLoss && since !== null && !stretchLosingFast,
      spikeKg: spike !== null && Math.abs(spike) >= 0.9 ? spike : null,
      stale: latest ? diffDays(today, latest.date) > 7 : false,
    },
  };
}

export interface HeadlineWeight {
  kg: number;
  /** Weigh-in date. Null when the headline is the trend. */
  date: ISODate | null;
  /** `window` is the recent low, `latest` is the empty-window fallback, `trend` is the last resort. */
  source: 'window' | 'latest' | 'trend';
}

/** The primary weight to show: recent low, then the latest weigh-in, then the trend. */
export function headlineWeight(summary: Pick<WeightSummary, 'recentLow' | 'latestKg' | 'latestDate' | 'trendKg'>): HeadlineWeight | null {
  if (summary.recentLow) return { kg: summary.recentLow.kg, date: summary.recentLow.date, source: summary.recentLow.source };
  if (summary.latestKg != null) return { kg: summary.latestKg, date: summary.latestDate, source: 'latest' };
  if (summary.trendKg != null) return { kg: summary.trendKg, date: null, source: 'trend' };
  return null;
}
